import React, { useState } from 'react';
import { Container, Typography, Grid, TextField, Button, Card, CardContent, Alert } from '@mui/material';
import { useTranslation } from 'react-i18next';
import SEO from '../components/SEO';
import { getBreadcrumbSchema } from '../components/StructuredData';

const Contact: React.FC = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('idle');

    try {
      const response = await fetch('/api/send-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: 'contact',
          name: formData.name,
          email: formData.email,
          message: formData.message,
        }),
      });

      if (!response.ok) throw new Error('Failed to send email');

      setStatus('success');
      setFormData({ name: '', email: '', message: '' });
    } catch (error) {
      setStatus('error');
    }
  };

  return (
    <Container sx={{ py: 8 }}>
      <SEO
        title={t('seo.contact.title')}
        description={t('seo.contact.description')}
        canonicalPath="/contact"
        jsonLd={[
          getBreadcrumbSchema([
            { name: 'Home', url: '/' },
            { name: t('contact.title'), url: '/contact' },
          ]),
        ]}
      />

      <Typography variant="h3" component="h1" gutterBottom align="center">
        {t('contact.title')}
      </Typography>
      <Typography variant="h6" color="text.secondary" paragraph align="center">
        {t('contact.subtitle')}
      </Typography>

      <Grid container spacing={4}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h5" component="h2" gutterBottom>
                {t('contact.info.title')}
              </Typography>
              <Typography paragraph>
                {t('contact.info.address')}
              </Typography>
              <Typography paragraph>
                {t('contact.info.phone')}
              </Typography>
              <Typography>
                {t('contact.info.email')}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <form onSubmit={handleSubmit}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label={t('contact.form.name')}
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label={t('contact.form.email')}
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label={t('contact.form.message')}
                      name="message"
                      multiline
                      rows={4}
                      value={formData.message}
                      onChange={handleChange}
                      required
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      fullWidth
                    >
                      {t('contact.form.submit')}
                    </Button>
                  </Grid>
                </Grid>
              </form>

              {status === 'success' && (
                <Alert severity="success" sx={{ mt: 2 }}>
                  {t('contact.form.success')}
                </Alert>
              )}
              {status === 'error' && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {t('contact.form.error')}
                </Alert>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
};

export default Contact;
